import { useMemo, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import type { TSavedFilter, TSavedFilterValues } from "@/zapi-frontend/shared/types/savedFilters";
import {
  GET_SAVED_FILTERS_KEY,
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useSavedFilters,
  useUpdateSavedFilter
} from "@/zapi-frontend/shared/services/useSavedFilters";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { showUndoToast } from "@/zapi-frontend/shared/ui/UndoToast/UndoToast";
import { findMatchingSavedFilter } from "../../lib/matchSavedFilter";
import { NamePromptModal } from "./_NamePromptModal";
import { useTranslation } from "react-i18next";

const UNDO_TOAST_DURATION_MS = 5000;

function extractErrorMessage(err: unknown): string | null {
  if (err instanceof AxiosError) return err.response?.data?.message ?? err.message ?? null;
  if (err instanceof Error) return err.message;
  return null;
}

export function useSavedFilterActions(activeFilters: TSavedFilterValues) {
  const { t } = useTranslation("projects");
  const { data: savedFilters = [], isLoading } = useSavedFilters();
  const updateMutation = useUpdateSavedFilter();
  const deleteMutation = useDeleteSavedFilter();

  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganizationStore();
  const savedFiltersKey = [GET_SAVED_FILTERS_KEY, currentOrganizationId];

  const [renameTarget, setRenameTarget] = useState<TSavedFilter | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const matched = useMemo(() => findMatchingSavedFilter(savedFilters, activeFilters), [savedFilters, activeFilters]);

  const handleRename = async (name: string) => {
    if (!renameTarget) return;
    setRenameError(null);
    try {
      await updateMutation.mutateAsync({ id: renameTarget.id, name });
      toast.success(t("savedFilters.renamed"));
      setRenameTarget(null);
    } catch (err) {
      setRenameError(extractErrorMessage(err) ?? t("savedFilters.renameFailed"));
    }
  };

  const removeSavedFilterFromCache = (target: TSavedFilter) => {
    queryClient.setQueryData<TSavedFilter[]>(savedFiltersKey, (current) =>
      (current ?? []).filter((f) => f.id !== target.id)
    );
  };

  const restoreSavedFilterToCache = (target: TSavedFilter) => {
    queryClient.setQueryData<TSavedFilter[]>(savedFiltersKey, (current) => {
      const without = (current ?? []).filter((f) => f.id !== target.id);
      return [...without, target].sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
    });
  };

  const startRename = (target: TSavedFilter) => {
    setRenameError(null);
    setRenameTarget(target);
  };

  const remove = (target: TSavedFilter) => {
    removeSavedFilterFromCache(target);
    let cancelled = false;
    const commitTimer = window.setTimeout(() => {
      if (cancelled) return;
      deleteMutation.mutate(target.id, {
        onError: (err) => {
          restoreSavedFilterToCache(target);
          toast.error(extractErrorMessage(err) ?? t("savedFilters.deleteFailed"));
        }
      });
    }, UNDO_TOAST_DURATION_MS);
    showUndoToast({
      message: t("savedFilters.deleted", { name: target.name }),
      duration: UNDO_TOAST_DURATION_MS,
      onUndo: () => {
        cancelled = true;
        window.clearTimeout(commitTimer);
        restoreSavedFilterToCache(target);
      }
    });
  };

  const renameModal = (
    <NamePromptModal
      isOpen={renameTarget !== null}
      title={t("savedFilters.renameTitle")}
      confirmText={t("savedFilters.save")}
      initialName={renameTarget?.name ?? ""}
      isSubmitting={updateMutation.isPending}
      errorMessage={renameError}
      onClose={() => setRenameTarget(null)}
      onConfirm={handleRename}
    />
  );

  return { savedFilters, isLoading, matched, startRename, remove, renameModal };
}

export function useSaveCurrentFilters(activeFilters: TSavedFilterValues) {
  const { t } = useTranslation("projects");
  const createMutation = useCreateSavedFilter();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (name: string) => {
    setError(null);
    try {
      await createMutation.mutateAsync({ name, filters: activeFilters });
      toast.success(t("savedFilters.created"));
      setModalOpen(false);
    } catch (err) {
      setError(extractErrorMessage(err) ?? t("savedFilters.createFailed"));
    }
  };

  const open = () => {
    setError(null);
    setModalOpen(true);
  };

  const saveModal = (
    <NamePromptModal
      isOpen={modalOpen}
      title={t("savedFilters.saveTitle")}
      confirmText={t("savedFilters.save")}
      isSubmitting={createMutation.isPending}
      errorMessage={error}
      onClose={() => setModalOpen(false)}
      onConfirm={handleSave}
    />
  );

  return { open, saveModal };
}
